'use client';

import {
  Fragment,
  type MouseEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';
import { useSearchParams } from 'next/navigation';
import { ChevronLeft } from 'lucide-react';
import { Button, Heading, ShowHide, Text } from '@zillow/constellation-10';
import { useTranslations } from 'next-intl';
import { cx } from '@/styled-system/css';

import { ListingCard } from '@/src/components/ListingCard';
import { LucideIcon } from '@/src/components/LucideIcon';
import { DesktopStickyHeader } from '@/src/components/features/property/ListingPage';
import { FaqModule, getHdpFaqQuestions } from '@/src/components/features/property/FaqModule';
import { FAQ, moduleMetadata } from '@/src/components/features/property/moduleRegistry';
import { Module } from '@/src/components/features/property/ui/Module/Module';
import Footer from '@/src/components/ui/Footer';
import type { PropertyDetailListing } from '@/src/components/features/property/types';
import { Link, useRouter } from '@/src/i18n/navigation';
import { useSetActiveListing, useSetMapPinnedMarker } from '@/src/lib/hooks';
import { registerClientProfilerPage } from '@/src/lib/clientProfiler';
import {
  getActiveSearchHdpBackEntry,
  getActiveSearchHdpBackPath,
  markSearchHdpHistoryIntent,
  prepareSearchHdpHistoryEntry,
  queueSearchHdpBackScrollRestore,
} from '@/src/lib/navigation/searchHdpHistory';
import { buildSrpFilterCarryoverHref } from '@/src/lib/navigation/srpQueryCarryover';
import { generateMarkerId } from '@/src/lib/utils/generateMarkerId';
import { getListingHdpHref } from '@/src/lib/utils/listingHref';
import { useHeaderVisibility } from '@/src/layouts/HeaderVisibilityContext';
import { searchContentBodyGutter, searchContentChromeGutter } from '@/src/layouts/searchContentGutters.styles';
import type { ExtendedListing } from '@/src/lib/api/endpoints/listing';
import type { ListingCardListing } from '@/src/components/ListingCard/types';
import { type BdpUnitGroup, type BdpUnitGroupKey, getBdpUnitKey } from './bdpUnits';
import {
  buildingTitle,
  content,
  contentStack,
  emptyState,
  footerWrapper,
  mobileHeaderActionGroup,
  pageHeader,
  pageRoot,
  topBar,
  unitCardsGrid,
  unitGroup,
  unitGroupHeader,
  unitGroups as unitGroupsStyles,
} from './buildingDetailsPage.styles';

type BuildingDetailsPageProps = {
  resourceOrMaloneId: string;
  building: PropertyDetailListing;
  unitGroups: Array<BdpUnitGroup>;
  allAvailableUnits: Array<PropertyDetailListing>;
  totalUnitCount: number;
  matchingUnitCount: number;
  hasActiveFilters: boolean;
  backHref: string;
  desktopHeaderActions?: ReactNode;
  mobileHeaderActions?: ReactNode;
  breadcrumbs?: ReactNode;
  seoFooter?: ReactNode;
};

const useIsomorphicLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect;

const faqModuleMetadata = moduleMetadata.find((module) => module.type === FAQ);

function formatAddressLine(listing: PropertyDetailListing): string {
  const cityState = [listing.address?.city, listing.address?.state].filter(Boolean).join(', ');
  return cityState;
}

function getBuildingName(listing: PropertyDetailListing): string {
  if (listing.displayName) return listing.displayName;
  if (listing.title) return listing.title;
  if (listing.address?.street && !listing.address.hideStreet) return listing.address.street;
  return listing.address?.city || 'Building';
}

function getGroupLabel(key: BdpUnitGroupKey, t: ReturnType<typeof useTranslations<'hdp'>>): string {
  switch (key) {
    case 'room':
      return t('bdpRoomForRentGroup');
    case 'studio':
      return t('bdpStudioGroup');
    case 'one':
      return t('bdpOneBedroomGroup');
    case 'two':
      return t('bdpTwoBedroomsGroup');
    case 'three':
      return t('bdpThreeBedroomsGroup');
    case 'fourPlus':
      return t('bdpFourPlusBedroomsGroup');
  }
}

function unitHref(unit: PropertyDetailListing): string {
  return getListingHdpHref(unit) || (unit.aliasEncoded ? `/listings/${unit.aliasEncoded}` : '/');
}

export function BuildingDetailsPage({
  resourceOrMaloneId,
  building,
  unitGroups,
  allAvailableUnits,
  totalUnitCount,
  matchingUnitCount,
  hasActiveFilters,
  backHref,
  desktopHeaderActions,
  mobileHeaderActions,
  breadcrumbs,
  seoFooter,
}: BuildingDetailsPageProps) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const tHdp = useTranslations('hdp');
  const tSearch = useTranslations('search');
  const { setHideHeaderOnMobile } = useHeaderVisibility();
  const setPinnedMarker = useSetMapPinnedMarker();
  const setActiveListing = useSetActiveListing();
  const heading = getBuildingName(building);
  const addressLine = formatAddressLine(building);
  // Registry gate plus a question-count check so the Module heading never
  // renders over an empty FAQ.
  const showFaq = useMemo(
    () => faqModuleMetadata?.shouldRender?.(building) === true && getHdpFaqQuestions(building).length > 0,
    [building],
  );
  const shouldShowAllAvailableRentals = hasActiveFilters && totalUnitCount > matchingUnitCount;
  const backHrefWithSearchState = buildSrpFilterCarryoverHref(backHref, searchParams);
  const [isAllAvailableHidden, setIsAllAvailableHidden] = useState(true);
  const showAllAvailableButtonLabel = isAllAvailableHidden
    ? tHdp('bdpShowAllAvailableRentals', { count: totalUnitCount })
    : tHdp('bdpHideAllAvailableRentals');
  const showAllAvailableButtonAriaLabel = isAllAvailableHidden
    ? tHdp('bdpShowAllAvailableRentalsAria', { count: totalUnitCount })
    : tHdp('bdpHideAllAvailableRentalsAria');

  useIsomorphicLayoutEffect(() => {
    setHideHeaderOnMobile(true);
    return () => {
      setHideHeaderOnMobile(false);
    };
  }, [setHideHeaderOnMobile]);

  useEffect(() => {
    registerClientProfilerPage('hotpads-bdp');
  }, []);

  useEffect(() => {
    setIsAllAvailableHidden(true);
  }, [matchingUnitCount, totalUnitCount]);

  useIsomorphicLayoutEffect(() => {
    const isInFlowSearchHdp = prepareSearchHdpHistoryEntry();
    if (isInFlowSearchHdp) {
      const searchPath = getActiveSearchHdpBackPath();
      if (searchPath) {
        router.prefetch(searchPath as Parameters<typeof router.prefetch>[0]);
      }
    }
  }, [resourceOrMaloneId, router]);

  const navigateBack = useCallback(() => {
    if (prepareSearchHdpHistoryEntry()) {
      const backEntry = getActiveSearchHdpBackEntry();
      queueSearchHdpBackScrollRestore();
      if (backEntry) {
        window.history.go(-backEntry.historyDepth);
        return;
      }
    }

    router.push(backHrefWithSearchState);
  }, [backHrefWithSearchState, router]);

  const handleBackClick = useCallback(
    (event: MouseEvent<HTMLAnchorElement>) => {
      if (
        event.defaultPrevented ||
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      ) {
        return;
      }

      event.preventDefault();
      navigateBack();
    },
    [navigateBack],
  );

  const handleUnitNavigate = useCallback(
    (listing: ListingCardListing) => {
      const unit = listing as PropertyDetailListing;
      const href = unitHref(unit);
      markSearchHdpHistoryIntent(href);
      if (unit.geo) {
        const markerId = generateMarkerId(unit as ExtendedListing);
        setPinnedMarker(markerId);
        setActiveListing({
          listingId: markerId,
          lat: unit.geo.lat,
          lng: unit.geo.lon,
        });
      }
    },
    [setActiveListing, setPinnedMarker],
  );

  return (
    <main className={pageRoot} aria-labelledby="bdp-heading">
      <DesktopStickyHeader
        backHref={backHrefWithSearchState}
        onBackClick={handleBackClick}
        backLabel={tSearch('backToSearch')}
        backAriaLabel={tSearch('backToSearchResults')}
        forceUtilityLabels
        actions={desktopHeaderActions}
      />

      <div className={cx(topBar, searchContentChromeGutter)}>
        <Button asChild tone="neutral" emphasis="bare" size="sm" icon={<LucideIcon icon={ChevronLeft} size="sm" />}>
          <Link href={backHrefWithSearchState} onClick={handleBackClick} aria-label={tSearch('backToSearchResults')}>
            {tSearch('backToSearch')}
          </Link>
        </Button>
        {mobileHeaderActions ? <div className={mobileHeaderActionGroup}>{mobileHeaderActions}</div> : null}
      </div>

      <div className={cx(content, searchContentBodyGutter)}>
        <header className={pageHeader}>
          <div className={buildingTitle}>
            <Heading id="bdp-heading" level={1} textStyle="heading-xs">
              {heading}
            </Heading>
            {addressLine && (
              <Text asChild textStyle="body-sm-bold">
                <p>{addressLine}</p>
              </Text>
            )}
          </div>
          <Text asChild color="text.subtle">
            <p>
              {hasActiveFilters
                ? tHdp('bdpMatchingUnitsDescription', { count: matchingUnitCount })
                : tHdp('bdpAvailableUnitsDescription', { count: totalUnitCount })}
            </p>
          </Text>
        </header>

        {unitGroups.length > 0 ? (
          <div className={unitGroupsStyles}>
            {unitGroups.map((group) => (
              <section key={group.key} className={unitGroup} aria-labelledby={`bdp-group-${group.key}`}>
                <div className={unitGroupHeader}>
                  <div>
                    <Heading id={`bdp-group-${group.key}`} level={2} textStyle="heading-xs">
                      {getGroupLabel(group.key, tHdp)}
                    </Heading>
                    <Text asChild color="text.subtle" textStyle="body-sm">
                      <p>{tHdp('bdpUnitsCount', { count: group.units.length })}</p>
                    </Text>
                  </div>
                  {group.priceDisplay && (
                    <Text asChild color="text.subtle">
                      <p>{group.priceDisplay}</p>
                    </Text>
                  )}
                </div>

                <div className={unitCardsGrid}>
                  {group.units.map((unit, index) => (
                    <ListingCard
                      key={getBdpUnitKey(unit, index)}
                      listing={unit}
                      href={unitHref(unit)}
                      priority={index === 0}
                      onNavigate={handleUnitNavigate}
                    />
                  ))}
                </div>
              </section>
            ))}
          </div>
        ) : (
          <section className={emptyState} role="status" aria-live="polite">
            <Heading level={2} textStyle="heading-xs">
              {tHdp('bdpNoMatchingUnitsTitle')}
            </Heading>
            <Text asChild color="text.subtle">
              <p>{tHdp('bdpNoMatchingUnitsDescription')}</p>
            </Text>
          </section>
        )}

        {shouldShowAllAvailableRentals && (
          <ShowHide.Root
            id="bdp-all-available-rentals"
            appearance="block"
            isHidden={isAllAvailableHidden}
            onDisplayChange={() => setIsAllAvailableHidden((prev) => !prev)}
            renderButton={() => (
              <ShowHide.Button aria-controls="bdp-all-available-rentals" aria-label={showAllAvailableButtonAriaLabel}>
                {showAllAvailableButtonLabel}
              </ShowHide.Button>
            )}
          >
            <div className={contentStack}>
              <ShowHide.Hidden>
                <section aria-label={tHdp('bdpAllAvailableRentalsLabel')} className={unitCardsGrid}>
                  {allAvailableUnits.map((unit, index) => (
                    <ListingCard
                      key={`all-${getBdpUnitKey(unit, index)}`}
                      listing={unit}
                      href={unitHref(unit)}
                      priority={false}
                      onNavigate={handleUnitNavigate}
                    />
                  ))}
                </section>
              </ShowHide.Hidden>
            </div>
          </ShowHide.Root>
        )}

        {showFaq && faqModuleMetadata && (
          <Module
            id={faqModuleMetadata.id}
            title={faqModuleMetadata.titleKey ? tHdp(faqModuleMetadata.titleKey) : faqModuleMetadata.title}
          >
            <FaqModule listing={building} />
          </Module>
        )}

        {breadcrumbs}

        <Fragment key="seo-footer">{seoFooter}</Fragment>

        <Fragment key="site-footer">
          <div className={footerWrapper}>
            <Footer showCrossbrand={false} />
          </div>
        </Fragment>
      </div>
    </main>
  );
}
